import React, { Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { Calendar, BookOpen, Clock, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
import { PageHero } from '../components/PageHero';
interface MagazineProps {
  language: Language;
}
export function Magazine({
  language
}: MagazineProps) {
  const isPashto = language === 'pashto';
  const categories = isPashto ? [{
    title: 'مقالې',
    desc: 'د پښتو ژبې، تاریخ او کلتور په اړه ژورې څیړنې.'
  }, {
    title: 'شاعري',
    desc: 'د کلاسیک او معاصر پښتو شعرونو ټولګه.'
  }, {
    title: 'علمي',
    desc: 'د پوهنتون استادانو او څیړونکو علمي لیکنې.'
  }, {
    title: 'سرمقالې',
    desc: 'د پاڅون د مدیرانو نظرونه او تبصرې.'
  }] : [{
    title: 'Articles',
    desc: 'In-depth research on Pashto language, history, and culture.'
  }, {
    title: 'Poetry',
    desc: 'A collection of classic and contemporary Pashto poetry.'
  }, {
    title: 'Academic',
    desc: 'Scholarly writings by university professors and researchers.'
  }, {
    title: 'Editorials',
    desc: 'Opinions and commentary from the editors of Pasoon.'
  }];
  const issues2026 = [{
    periodEn: 'January – February',
    periodPs: 'جنوري – فبروري',
    yearEn: '2026',
    yearPs: '۲۰۲۶',
    volEn: 'Vol 21, Iss 1',
    volPs: 'ټوک ۲۱، ګڼه ۱',
    status: 'available',
    cover: 'https://cdn.magicpatterns.com/uploads/nkxSFVNX8PortnXbKmnDDH/image.png'
  }, {
    periodEn: 'March – April',
    periodPs: 'مارچ – اپریل',
    yearEn: '2026',
    yearPs: '۲۰۲۶',
    volEn: 'Vol 21, Iss 2',
    volPs: 'ټوک ۲۱، ګڼه ۲',
    status: 'available',
    cover: 'https://cdn.magicpatterns.com/uploads/sR4i2r4S1futQxXXFLun8U/Mar-April_26.jpg'
  }, {
    periodEn: 'May – June',
    periodPs: 'مۍ – جون',
    yearEn: '2026',
    yearPs: '۲۰۲۶',
    volEn: 'Vol 21, Iss 3',
    volPs: 'ټوک ۲۱، ګڼه ۳',
    status: 'coming_soon'
  }, {
    periodEn: 'July – August',
    periodPs: 'جولای – اګست',
    yearEn: '2026',
    yearPs: '۲۰۲۶',
    volEn: 'Vol 21, Iss 4',
    volPs: 'ټوک ۲۱، ګڼه ۴',
    status: 'coming_soon'
  }, {
    periodEn: 'September – October',
    periodPs: 'سپتمبر – اکتوبر',
    yearEn: '2026',
    yearPs: '۲۰۲۶',
    volEn: 'Vol 21, Iss 5',
    volPs: 'ټوک ۲۱، ګڼه ۵',
    status: 'coming_soon'
  }, {
    periodEn: 'November – December',
    periodPs: 'نومبر – دسمبر',
    yearEn: '2026',
    yearPs: '۲۰۲۶',
    volEn: 'Vol 21, Iss 6',
    volPs: 'ټوک ۲۱، ګڼه ۶',
    status: 'coming_soon'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <div className="pt-24 md:pt-28 bg-gray-50 min-h-screen pb-20" data-id="element-623">
      <PageHero language={language} titleEn="Magazine" titlePs="مجله" introEn="Explore current and upcoming issues" introPs="اوسنۍ او راتلونکې ګڼې وپلټئ" data-id="element-624" />

      {/* Intro Paragraph */}
      <section className="py-8 px-6" data-id="element-625">
        <div className="max-w-3xl mx-auto text-center" data-id="element-626">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-627">
            <p className={`text-gray-700 ${isPashto ? 'font-pashto leading-loose text-lg' : 'text-lg leading-relaxed'}`} data-id="element-628">
              {isPashto ? 'پاڅون مجله لومړی ځل په ۲۰۰۵ کال کې خپره شوه او له هغې وروسته یې پرته له ځنډه خپرونو ته دوام ورکړی دی. دا د پښتو یو له غوره مجلو څخه ګرځیدلې ده. موږ هیله لرو چې دا به د وخت په تیریدو سره نوره هم ښه شي او ډیر لوړ معیار به وساتي.' : 'Pasoon magazine was first published in 2005 and since then has continued publishing without interruption. It has become one of the best Pushto Magazines. We hope it will improve more with time and will maintain a very high standard.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Issues Timeline Grid */}
      <section className="py-8 px-6" data-id="element-629">
        <div className="max-w-5xl mx-auto" data-id="element-630">
          {/* Year Header */}
          <div className="mb-6 text-start" data-id="element-631">
            <h2 className={`text-4xl md:text-5xl font-bold text-pasoon-dark inline-block relative ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-632">
              {isPashto ? '۲۰۲۶ کال' : 'Year 2026'}
              <span className="absolute -bottom-3 start-0 w-1/2 h-1.5 bg-pasoon-red rounded-full" data-id="element-633"></span>
            </h2>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-634">
            {issues2026.map((issue, idx) => {
            const isAvailable = issue.status === 'available';
            return <motion.div key={idx} variants={itemVariants} className={`relative p-6 rounded-2xl flex flex-col h-full transition-all ${isAvailable ? 'bg-white border border-gray-200 shadow-lg shadow-gray-200/50 hover:-translate-y-1' : 'bg-gray-50 border-2 border-dashed border-gray-200 opacity-80'}`} data-id="element-635">
                  {/* Cover Image */}
                  {issue.cover && <div className="mb-4 -mx-2 -mt-2" data-id="element-636">
                      <img src={issue.cover} alt={`Pasoon ${isPashto ? issue.periodPs : issue.periodEn} ${isPashto ? issue.yearPs : issue.yearEn}`} className="w-full h-auto rounded-xl object-contain" loading="lazy" data-id="element-637" />
                    </div>}

                  {/* Badge */}
                  <div className="mb-5" data-id="element-638">
                    {isAvailable ? <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-pasoon-muted text-xs font-bold ${isPashto ? 'font-pashto' : 'uppercase tracking-wider'}`} data-id="element-639">
                        <CheckCircle2 className="w-3.5 h-3.5" data-id="element-640" />
                        {isPashto ? 'شتون لري' : 'Available'}
                      </span> : <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-200 text-gray-600 text-xs font-bold ${isPashto ? 'font-pashto' : 'uppercase tracking-wider'}`} data-id="element-641">
                        <Clock className="w-3.5 h-3.5" data-id="element-642" />
                        {isPashto ? 'ډیر ژر' : 'Coming Soon'}
                      </span>}
                  </div>

                  {/* Issue Info */}
                  <div className="flex-grow" data-id="element-643">
                    <div className="flex items-center gap-2 mb-2" data-id="element-644">
                      <Calendar className={`w-5 h-5 ${isAvailable ? 'text-pasoon-red' : 'text-gray-400'}`} data-id="element-645" />
                      <h3 className={`text-xl font-bold ${isAvailable ? 'text-pasoon-dark' : 'text-gray-500'} ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-646">
                        {isPashto ? issue.periodPs : issue.periodEn}{' '}
                        {isPashto ? issue.yearPs : issue.yearEn}
                      </h3>
                    </div>
                    <p className={`text-sm ${isAvailable ? 'text-gray-500' : 'text-gray-400'} ${isPashto ? 'font-pashto' : ''}`} data-id="element-647">
                      {isPashto ? issue.volPs : issue.volEn}
                    </p>
                  </div>

                  {/* Action / Label */}
                  <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-648">
                    {isAvailable ? <Link to={issue.periodEn === 'January – February' ? '/jan-feb-2026' : '/current-issue'} className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-pasoon-red text-white rounded-xl font-medium hover:bg-red-800 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-649">
                        <BookOpen className="w-4 h-4" data-id="element-650" />
                        {isPashto ? 'ګڼه ولولئ' : 'Read Issue'}
                      </Link> : <div className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-100 text-gray-500 rounded-xl font-medium ${isPashto ? 'font-pashto' : ''}`} data-id="element-651">
                        {isPashto ? 'د خپرولو په بهیر کې' : 'To be published'}
                      </div>}
                  </div>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-8 px-6" data-id="element-652">
        <div className="max-w-5xl mx-auto" data-id="element-653">
          <div className="mb-6 text-center" data-id="element-654">
            <h2 className={`text-3xl font-bold text-pasoon-dark mb-4 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-655">
              {isPashto ? 'د مجلې برخې' : 'Magazine Sections'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-656">
            {categories.map((cat, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-657">
                <h3 className={`text-xl font-bold text-pasoon-red mb-3 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-658">
                  {cat.title}
                </h3>
                <p className={`text-gray-600 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-659">
                  {cat.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </div>;
}